import { useConfigContext } from "@/state/configContext";
import { assert } from "@/helpers/helpers";
import { canBe } from "@/admin/admin";
import { downloadZip } from "@/pedidos/downloadZip";
import { sitioConfig } from "@/sitio";
import { useAdminContext } from "@/state/adminContext";
import { clearFlashMessage, setFlashMessage } from "@/state/flash";
import { queryClient, server, useURL } from "@/state/server";
import {
  AdminPedidoZod,
  ClientesHabitualesZod,
  type Admin,
  type AdminPedido,
} from "@/state/types";
import type { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  faBan,
  faDollar,
  faDownload,
  faList,
  faMailForward,
  faPrint,
  faSpinner,
  faTicket,
} from "@fortawesome/free-solid-svg-icons";
import {
  FontAwesomeIcon,
  type FontAwesomeIconProps,
} from "@fortawesome/react-fontawesome";
import { Link } from "@tanstack/react-router";
import { Button, ButtonToolbar } from "react-bootstrap";
import type { ButtonVariant } from "react-bootstrap/esm/types";
import { Tooltip } from "react-tooltip";
import { z } from "zod";
import { POST } from "@/state/api";

const PedidosResultZod = z.object({
  pedidos: z.array(AdminPedidoZod),
  clientes: ClientesHabitualesZod.optional(),
});

export function ListaPedidos({
  url,
  showTotal,
}: {
  url: string;
  showTotal?: boolean;
}) {
  const { admin } = useAdminContext();
  const config = useConfigContext();
  const { data, isError, isLoading } = useURL(url, PedidosResultZod, 30_000);

  if (isError) return <div>ERROR</div>;
  if (isLoading) return <div>Loading...</div>;
  assert(data);
  const { pedidos, clientes } = data;

  const moneda = sitioConfig.moneda;

  return (
    <div>
      <div className="table-responsive">
        <table className="table table-striped table-hover">
          <thead>
            <tr>
              <th>Pedido</th>
              <th>Fecha</th>
              <th>Cliente</th>
              <th>Importe</th>
              <th>Descuento</th>
              <th>Neto</th>
              <th>Vendedor</th>
              <th>Obs</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {pedidos.map((p) => (
              <tr key={p.id}>
                <td>
                  {p.codLocal} {p.id}
                </td>
                <td>{p.fecha}</td>
                <td>
                  {p.cliente.nombre}
                  {clientes &&
                    (clientes[p.cliente.id].P > 0 ||
                      clientes[p.cliente.id].fotos > 0) &&
                    ` (${clientes?.[p.cliente.id].P} -> ${clientes?.[p.cliente.id].fotos})`}
                </td>
                <td style={{ textAlign: "right" }}>
                  {p.subtotal}&nbsp;{moneda}
                </td>
                <td style={{ textAlign: "right" }}>
                  {p.descuento}&nbsp;{moneda}
                </td>
                <td style={{ textAlign: "right" }}>
                  {p.subtotal - p.descuento}&nbsp;{moneda}
                </td>
                <td>{p.vendedor && config.fotografos?.[p.vendedor].nombre}</td>
                <td>{p.obs}</td>
                <td>
                  <Acciones pedido={p} admin={admin} url={url} />
                </td>
              </tr>
            ))}
            {showTotal && (
              <>
                <tr />
                <tr className="total">
                  <td colSpan={3}>Total</td>
                  <td>
                    {sum(pedidos.map((p) => p.subtotal))}
                    &nbsp;{moneda}
                  </td>
                  <td>
                    {sum(pedidos.map((p) => p.descuento))}
                    &nbsp;{moneda}
                  </td>
                  <td>
                    {sum(pedidos.map((p) => p.subtotal - p.descuento))}
                    &nbsp;{moneda}
                  </td>
                  <td colSpan={3} />
                </tr>
                <tr>
                  <th colSpan={3}>Vendedor</th>
                  <th>Importe</th>
                  <th>Descuento</th>
                  <th>Neto</th>
                  <th colSpan={3} />
                </tr>
                {Object.entries(group(pedidos, (x) => x.vendedor)).map(
                  ([vendedor, pedidosV]) => (
                    <tr key={vendedor}>
                      <td colSpan={3}>
                        {config.fotografos[Number(vendedor)]?.nombre}
                      </td>
                      <td style={{ textAlign: "right" }}>
                        {sum(pedidosV.map((p) => p.subtotal))}
                        &nbsp;
                        {moneda}
                      </td>
                      <td style={{ textAlign: "right" }}>
                        {sum(pedidosV.map((p) => p.descuento))}&nbsp;
                        {moneda}
                      </td>
                      <td style={{ textAlign: "right" }}>
                        {sum(pedidosV.map((p) => p.subtotal - p.descuento))}
                        &nbsp;{moneda}
                      </td>
                      <td colSpan={3} />
                    </tr>
                  )
                )}
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function sum(v: number[]) {
  return v.reduce((a, b) => a + b, 0);
}

function group<T>(values: T[], fn: (a: T) => unknown) {
  const result: Record<string, T[]> = {};

  for (const v of values) {
    const k = `${fn(v)}`;
    result[k] = [...(result[k] || []), v];
  }
  return result;
}

function Acciones({
  pedido,
  admin,
  url,
}: {
  pedido: AdminPedido;
  admin: Admin;
  url: string;
}) {
  const canDo = (permiso: string) => canBe(admin, [permiso]);
  const config = useConfigContext();

  const anularContent = (
    <Accion
      titulo="Anular"
      variant="danger"
      icon={faBan}
      onClick={pedido.pagado ? undefined : () => anularPedido(pedido.id, url)}
      pedidoId={pedido.id}
    />
  );

  const reportStatus = (x: string) =>
    x ? setFlashMessage({ message: x, level: "success" }) : clearFlashMessage();

  return (
    <ButtonToolbar>
      <Link to="/admin/pedido" search={{ id: pedido.id }}>
        <Accion
          titulo="Ver"
          variant="primary"
          icon={faList}
          pedidoId={pedido.id}
        />
      </Link>
      {pedido.pagado ? (
        <Link to="/admin/pedido/anular" search={{ id: pedido.id }}>
          {anularContent}
        </Link>
      ) : (
        anularContent
      )}
      {pedido.pagado && canDo("cajero") && !!pedido.notificacion && (
        <Accion
          titulo="Renotificar"
          variant="primary"
          icon={faMailForward}
          onClick={() => alert(`Renotificar ${pedido.id}`)}
          pedidoId={pedido.id}
        />
      )}
      {pedido.pagado && canDo("cajero") && !pedido.notificacion && (
        <Accion
          titulo="Notificación en curso"
          variant="primary"
          icon={faSpinner}
          iconProps={{ spin: true }}
          onClick={() => alert(`Renotificar ${pedido.id}`)}
          pedidoId={pedido.id}
        />
      )}
      {pedido.pagado && canDo("cajero") && !!pedido.ticket && (
        <Accion
          titulo="Reimprimir ticket"
          variant="primary"
          icon={faTicket}
          onClick={() => alert(`Reimprimir ticket ${pedido.id}`)}
          pedidoId={pedido.id}
        />
      )}

      {pedido.pagado && canDo("impresor") && (
        <Accion
          titulo="Descargar para imprimir"
          variant="primary"
          icon={faPrint}
          pedidoId={pedido.id}
          onClick={() => downloadZip(config, pedido, false, reportStatus)}
        />
      )}

      {pedido.pagado && canDo("impresor") && (
        <Accion
          titulo="Descargar completo"
          variant="primary"
          icon={faDownload}
          pedidoId={pedido.id}
          onClick={() => downloadZip(config, pedido, true, reportStatus)}
        />
      )}

      {pedido.pagado && canDo("cajero") && !pedido.factura && (
        <Accion
          titulo="Facturar"
          variant="primary"
          icon={faDollar}
          onClick={async () => {
            if (
              pedido.obs?.match(/- Tarjetas$/) ||
              pedido.obs?.match(/- Banco$/) ||
              window.confirm("Seguro facturar a " + pedido.obs + "?")
            ) {
              await POST("/admin/pedido/facturar/:id", {
                p: { id: pedido.id },
              });
              queryClient.invalidateQueries({ queryKey: [url] });
            }
          }}
          pedidoId={pedido.id}
        />
      )}

      {pedido.pagado && canDo("cajero") && !!pedido.factura && (
        <a href={`${server}/admin/factura/${pedido.factura.id}`}>
          <Accion
            titulo={`Factura ${pedido.factura?.pto_venta}-${pedido.factura?.numero}, ${pedido.factura?.fecha}, ${pedido.factura?.monto}`}
            variant="info"
            icon={faDollar}
            pedidoId={pedido.id}
          />
        </a>
      )}
    </ButtonToolbar>
  );
}

function Accion({
  variant,
  pedidoId,
  titulo,
  icon,
  iconProps,
  onClick,
}: {
  titulo: string;
  variant: ButtonVariant;
  icon: IconProp;
  iconProps?: Partial<FontAwesomeIconProps>;
  pedidoId: number;
  onClick?: () => unknown;
}) {
  const id = `but_${pedidoId}_${titulo.replace(/[^A-Za-z]/g, "")}`;

  return (
    <>
      <Button
        style={{ marginLeft: 5 }}
        variant={variant}
        id={id}
        onClick={onClick}
      >
        <FontAwesomeIcon icon={icon} fixedWidth {...(iconProps || {})} />
      </Button>
      <Tooltip anchorSelect={id} place="top">
        {titulo}
      </Tooltip>
    </>
  );
}

async function anularPedido(pedidoId: number, url: string) {
  const res = await POST(`/admin/pedido/anular/:id`, { p: { id: pedidoId } });
  queryClient.invalidateQueries({ queryKey: [url] });
  return res;
}
